import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { extractExifFromFile } from "../../hooks/useExifExtract";
import { posts as postsApi, upload as uploadApi } from "../../services/api";
import LocationPicker from "./LocationPicker";
import CategoryPicker from "./CategoryPicker";
import CuisinePicker from "./CuisinePicker";
import { CATEGORIES } from "../../data/seedData";
import {
  Images,
  ChevronLeft,
  ChevronRight,
  X,
  MapPin,
  Cpu,
  Clock,
  Loader,
  RotateCcw,
  ThumbsUp,
  Check,
  Upload,
  AlertTriangle,
} from "lucide-react";
import { useTranslation } from "react-i18next";

function BatchUploadView({ onBack }) {
  const fileInputRef = useRef(null);
  const { token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Each item: { file, preview, exif, caption, category, worthIt, wouldReturn, manualLocation }
  const [items, setItems] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState({ done: 0, total: 0 });
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handleFilesSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setLoading(true);
    setError(null);

    const newItems = await Promise.all(
      files.map(async (file) => {
        const preview = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (ev) => resolve(ev.target.result);
          reader.readAsDataURL(file);
        });
        const exif = await extractExifFromFile(file);
        return {
          file,
          preview,
          exif,
          caption: "",
          category: null,
          cuisine: null,
          worthIt: null,
          wouldReturn: null,
          manualLocation: null,
        };
      })
    );

    setItems(newItems);
    setCurrentIdx(0);
    setLoading(false);
  };

  const removeItem = (idx) => {
    setItems((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) return next;
      return next;
    });
    setCurrentIdx((prev) => {
      if (idx < prev) return prev - 1;
      if (idx === prev && prev >= items.length - 1) return Math.max(0, items.length - 2);
      return prev;
    });
  };

  const current = items[currentIdx];

  const updateCurrent = (updates) => {
    setItems((prev) =>
      prev.map((item, i) => (i === currentIdx ? { ...item, ...updates } : item))
    );
  };

  const hasLocation = (item) => item.exif?.hasGps || item.manualLocation;

  const getLocation = (item) => {
    if (item.exif?.hasGps) {
      return { latitude: item.exif.latitude, longitude: item.exif.longitude };
    }
    return item.manualLocation;
  };

  const isItemReady = (item) =>
    hasLocation(item) &&
    item.category &&
    item.caption.trim() &&
    item.worthIt !== null &&
    item.wouldReturn !== null;

  const allReady = items.length > 0 && items.every(isItemReady);
  const notReadyCount = items.filter((item) => !isItemReady(item)).length;


  const handleSubmitAll = async () => {
    setSubmitting(true);
    setSubmitProgress({ done: 0, total: items.length });
    setError(null);

    try {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const location = getLocation(item);
        const { imageUrl, thumbnailUrl } = await uploadApi.image(token, item.file);

        await postsApi.create(token, {
          imageUrl,
          thumbnailUrl,
          caption: item.caption.trim(),
          category: item.category,
          cuisine: item.category === "food" ? item.cuisine : null,
          latitude: location.latitude,
          longitude: location.longitude,
          worthIt: item.worthIt,
          wouldReturn: item.wouldReturn,
          locationName: "",
          countryCode: "",
          takenAt: item.exif?.takenAt || null,
          exifData: item.exif || { hasGps: false, device: "Unknown" },
        });

        setSubmitProgress({ done: i + 1, total: items.length });
      }
      setDone(true);
    } catch (err) {
      setError(t("batch.failedOnPhoto", { num: submitProgress.done + 1, error: err.message }));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-th-bg px-6">
        <div className="w-16 h-16 rounded-2xl bg-th-accent-dim border border-th-accent-border flex items-center justify-center mb-6">
          <Check size={28} className="text-th-accent" />
        </div>
        <h2 className="text-xl font-bold text-th-text">
          {t("batch.photosPinned", { count: items.length })}
        </h2>
        <p className="text-sm text-th-muted mt-2 text-center">
          {t("batch.experiencesLive")}
        </p>
        <button
          onClick={() => navigate("/")}
          className="mt-6 px-8 py-3 bg-th-accent text-th-bg rounded-xl text-sm font-semibold"
        >
          {t("batch.viewMap")}
        </button>
        <button
          onClick={onBack}
          className="mt-3 px-6 py-2 text-sm font-medium text-th-muted"
        >
          {t("batch.uploadMore")}
        </button>
      </div>
    );
  }

  // Selection screen
  if (items.length === 0) {
    return (
      <div className="h-full flex flex-col bg-th-bg">
        <div className="px-4 py-4 pt-[max(1rem,env(safe-area-inset-top))] flex items-center gap-3">
          <button onClick={onBack} className="text-th-muted">
            <ChevronLeft size={20} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-th-text">{t("batch.title")}</h1>
            <p className="text-xs text-th-muted mt-0.5 font-mono uppercase tracking-wider">
              {t("batch.subtitle")}
            </p>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-4">
          {loading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader size={32} className="text-th-accent animate-spin" />
              <p className="text-sm text-th-muted font-mono">
                {t("upload.readingExif")}
              </p>
            </div>
          ) : (
            <div
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-64 border border-dashed border-th-border rounded-2xl flex flex-col items-center justify-center cursor-pointer hover:border-th-text-sec active:bg-th-surface transition-all"
            >
              <Images size={32} className="text-th-muted mb-3" />
              <p className="text-sm text-th-text-sec">
                {t("batch.tapToSelect")}
              </p>
              <p className="text-xs text-th-muted mt-1 font-mono">
                {t("batch.exifAll")}
              </p>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFilesSelect}
            className="hidden"
            data-testid="batch-file-input"
          />
        </div>
      </div>
    );
  }

  // Tagging flow — swipe through each photo
  return (
    <div className="h-full flex flex-col bg-th-bg overflow-hidden">
      {/* Header with progress */}
      <div className="px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] flex items-center justify-between border-b border-th-border">
        <button onClick={onBack} className="text-th-muted">
          <X size={20} />
        </button>
        <span className="text-sm font-mono text-th-text-sec">
          {currentIdx + 1} / {items.length}
        </span>
        <button
          onClick={handleSubmitAll}
          disabled={!allReady || submitting}
          className="text-sm font-semibold text-th-accent disabled:opacity-30"
        >
          {submitting
            ? `${submitProgress.done}/${submitProgress.total}`
            : `${t("batch.pinAll")} (${items.length})`}
        </button>
      </div>

      {/* Photo strip — thumbnails */}
      <div className="flex gap-1.5 px-4 py-2 overflow-x-auto no-scrollbar">
        {items.map((item, i) => {
          const ready = isItemReady(item);
          const active = i === currentIdx;
          return (
            <button
              key={i}
              onClick={() => setCurrentIdx(i)}
              className={`relative flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                active
                  ? "border-th-accent"
                  : ready
                  ? "border-emerald-500/40"
                  : "border-red-500/50"
              }`}
            >
              <img
                src={item.preview}
                alt=""
                className={`w-full h-full object-cover ${!ready && !active ? "opacity-60" : ""}`}
              />
              {ready && !active && (
                <div className="absolute top-0.5 right-0.5 w-3 h-3 bg-emerald-500 rounded-full flex items-center justify-center">
                  <Check size={8} className="text-white" strokeWidth={3} />
                </div>
              )}
              {!ready && !active && (
                <div className="absolute top-0.5 right-0.5 w-3 h-3 bg-red-500 rounded-full" />
              )}
            </button>
          );
        })}
      </div>
      {/* Jump to next incomplete */}
      {!allReady && (
        <button
          onClick={() => {
            const nextIncomplete = items.findIndex((item, i) => i > currentIdx && !isItemReady(item));
            const firstIncomplete = nextIncomplete !== -1 ? nextIncomplete : items.findIndex((item) => !isItemReady(item));
            if (firstIncomplete !== -1) setCurrentIdx(firstIncomplete);
          }}
          className="mx-4 text-xs text-red-400 font-mono underline"
        >
          {t("batch.jumpIncomplete")}
        </button>
      )}

      {/* Current photo editor */}
      {items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="text-th-muted text-sm">{t("batch.allRemoved")}</p>
        </div>
      ) : (
      <div className="flex-1 overflow-y-auto px-4 pb-8 space-y-3">
        <div className="relative">
          <img
            src={current.preview}
            alt="Preview"
            className="w-full max-h-64 object-contain rounded-2xl bg-th-surface"
          />
          {/* Remove this photo */}
          <button
            onClick={() => removeItem(currentIdx)}
            className="absolute top-2 right-2 w-7 h-7 bg-black/60 backdrop-blur-sm text-white rounded-full flex items-center justify-center"
          >
            <X size={14} />
          </button>
          {/* Nav arrows */}
          {currentIdx > 0 && (
            <button
              onClick={() => setCurrentIdx((i) => i - 1)}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 backdrop-blur-sm text-white rounded-xl flex items-center justify-center"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          {currentIdx < items.length - 1 && (
            <button
              onClick={() => setCurrentIdx((i) => i + 1)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/60 backdrop-blur-sm text-white rounded-xl flex items-center justify-center"
            >
              <ChevronRight size={16} />
            </button>
          )}
        </div>

        {/* EXIF info */}
        {current.exif && (
          <div className="bg-th-surface rounded-xl p-3 text-xs space-y-2 border border-th-border">
            <div className="flex justify-between items-center">
              <span className="text-th-muted flex items-center gap-1.5">
                <MapPin size={12} /> {t("upload.gps")}
              </span>
              <span
                className={`font-mono ${
                  current.exif.hasGps ? "text-th-accent" : "text-amber-500"
                }`}
              >
                {current.exif.hasGps ? t("upload.gpsLocked") : t("upload.gpsNotFound")}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-th-muted flex items-center gap-1.5">
                <Cpu size={12} /> {t("upload.device")}
              </span>
              <span className="text-th-text-sec font-mono">
                {current.exif.device}
              </span>
            </div>
            {current.exif.originalTimestamp && (
              <div className="flex justify-between items-center">
                <span className="text-th-muted flex items-center gap-1.5">
                  <Clock size={12} /> {t("upload.taken")}
                </span>
                <span className="text-th-text-sec font-mono">
                  {current.exif.originalTimestamp}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Manual location if no GPS */}
        {current.exif && !current.exif.hasGps && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={14} className="text-amber-500" />
              <p className="text-sm font-medium text-amber-500">
                {t("batch.noGpsWarning")}
              </p>
            </div>
            {/* Copy GPS from another photo */}
            {(() => {
              const withGps = items.filter((item, i) => i !== currentIdx && (item.exif?.hasGps || item.manualLocation));
              const noGpsIndices = items.reduce((acc, item, i) => {
                if (i !== currentIdx && !item.exif?.hasGps && !item.manualLocation) acc.push(i);
                return acc;
              }, []);
              if (withGps.length === 0) return null;
              // Check which photo's GPS is currently selected
              const selectedIdx = current.manualLocation
                ? withGps.findIndex((item) => {
                    const loc = getLocation(item);
                    return loc.latitude === current.manualLocation.latitude && loc.longitude === current.manualLocation.longitude;
                  })
                : -1;
              return (
                <div className="mb-3 space-y-2">
                  <p className="text-xs text-th-muted font-mono">{t("batch.useGpsFrom")}</p>
                  <div className="flex gap-2 overflow-x-auto no-scrollbar">
                    {withGps.map((item) => {
                      const idx = items.indexOf(item);
                      const loc = getLocation(item);
                      const isSelected = selectedIdx !== -1 && withGps[selectedIdx] === item;
                      return (
                        <button
                          key={idx}
                          onClick={() => updateCurrent({ manualLocation: { latitude: loc.latitude, longitude: loc.longitude } })}
                          className={`flex-shrink-0 flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-mono transition-all ${
                            isSelected
                              ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-500"
                              : "bg-th-surface border-th-border text-th-muted"
                          }`}
                        >
                          <img src={item.preview} alt="" className="w-7 h-7 rounded object-cover" />
                          #{idx + 1}
                        </button>
                      );
                    })}
                  </div>
                  {current.manualLocation && noGpsIndices.length > 0 && (
                    <button
                      onClick={() => {
                        setItems((prev) =>
                          prev.map((item, i) =>
                            noGpsIndices.includes(i)
                              ? { ...item, manualLocation: { ...current.manualLocation } }
                              : item
                          )
                        );
                      }}
                      className="w-full px-3 py-2 text-xs text-emerald-400 font-mono border border-emerald-500/20 rounded-lg"
                    >
                      {t("batch.applyToMissing", { count: noGpsIndices.length })}
                    </button>
                  )}
                </div>
              );
            })()}
            <LocationPicker
              value={current.manualLocation}
              onChange={(loc) => updateCurrent({ manualLocation: loc })}
            />
          </div>
        )}

        {/* Category */}
        <div>
          <p className="text-sm font-medium text-th-text mb-2">{t("upload.category")}</p>
          <CategoryPicker
            categories={CATEGORIES}
            value={current.category}
            onChange={(cat) => updateCurrent({ category: cat, ...(cat !== "food" ? { cuisine: null } : {}) })}
          />
        </div>

        {/* Cuisine (food only) */}
        {current.category === "food" && (
          <div>
            <p className="text-sm font-medium text-th-text mb-2">{t("upload.cuisine")}</p>
            <CuisinePicker value={current.cuisine} onChange={(c) => updateCurrent({ cuisine: c })} />
          </div>
        )}

        {/* Worth it */}
        <div>
          <p className="text-sm font-medium text-th-text mb-2">{t("upload.worthIt")}</p>
          <div className="flex gap-2">
            <button
              onClick={() => updateCurrent({ worthIt: true })}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                current.worthIt === true
                  ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-500"
                  : "bg-th-surface border-th-border text-th-muted hover:text-th-text-sec"
              }`}
            >
              <ThumbsUp size={14} />
              {t("upload.worthItYes")}
            </button>
            <button
              onClick={() => updateCurrent({ worthIt: false })}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                current.worthIt === false
                  ? "bg-red-500/15 border-red-500/40 text-red-500"
                  : "bg-th-surface border-th-border text-th-muted hover:text-th-text-sec"
              }`}
            >
              <X size={14} />
              {t("upload.worthItNo")}
            </button>
          </div>
        </div>

        {/* Would return */}
        <div>
          <p className="text-sm font-medium text-th-text mb-2">
            {t("upload.wouldReturn")}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => updateCurrent({ wouldReturn: true })}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                current.wouldReturn === true
                  ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-500"
                  : "bg-th-surface border-th-border text-th-muted hover:text-th-text-sec"
              }`}
            >
              <RotateCcw size={14} />
              {t("upload.wouldReturnYes")}
            </button>
            <button
              onClick={() => updateCurrent({ wouldReturn: false })}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-all ${
                current.wouldReturn === false
                  ? "bg-red-500/15 border-red-500/40 text-red-500"
                  : "bg-th-surface border-th-border text-th-muted hover:text-th-text-sec"
              }`}
            >
              <X size={14} />
              {t("upload.wouldReturnNo")}
            </button>
          </div>
        </div>

        {/* Caption */}
        <div>
          <p className="text-sm font-medium text-th-text mb-2">{t("upload.caption")}</p>
          <textarea
            value={current.caption}
            onChange={(e) => updateCurrent({ caption: e.target.value })}
            placeholder={t("upload.captionPlaceholder")}
            maxLength={280}
            rows={2}
            className="w-full px-3 py-2.5 bg-th-surface border border-th-border rounded-xl text-base text-th-text placeholder-th-muted resize-none focus:outline-none focus:border-th-accent-border focus:ring-1 focus:ring-th-accent-border transition-all"
          />
          <div className="flex items-center justify-end">
            <p className="text-xs text-th-muted font-mono">
              {current.caption.length}/280
            </p>
          </div>
          {/* Copy caption from another photo */}
          {(() => {
            const withCaption = items.filter((item, i) => i !== currentIdx && item.caption.trim());
            if (withCaption.length === 0) return null;
            return (
              <div>
                <p className="text-xs text-th-muted font-mono mb-1.5">{t("batch.useCaptionFrom")}</p>
                <div className="flex gap-2 overflow-x-auto no-scrollbar">
                  {withCaption.map((item) => {
                    const idx = items.indexOf(item);
                    const isUsing = current.caption === item.caption && current.caption.trim();
                    return (
                      <button
                        key={idx}
                        onClick={() => updateCurrent({ caption: item.caption })}
                        className={`flex-shrink-0 flex items-center gap-2 px-2.5 py-1.5 rounded-lg border text-xs font-mono transition-all ${
                          isUsing
                            ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-500"
                            : "bg-th-surface border-th-border text-th-muted"
                        }`}
                      >
                        <img src={item.preview} alt="" className="w-7 h-7 rounded object-cover" />
                        #{idx + 1}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Bottom upload button */}
        {allReady && (
          <button
            onClick={handleSubmitAll}
            disabled={submitting}
            className="w-full py-3.5 bg-th-accent text-th-bg rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Upload size={16} />
            {submitting
              ? t("batch.uploadingProgress", { done: submitProgress.done, total: submitProgress.total })
              : t("batch.pinAllCount", { count: items.length })}
          </button>
        )}
        {!allReady && items.length > 0 && (
          <p className="text-xs text-th-muted text-center font-mono">
            {t("batch.needDetails", { count: notReadyCount })}
          </p>
        )}

        {error && (
          <p className="text-sm text-th-danger bg-th-danger-bg border border-th-danger-border px-3 py-2 rounded-xl">
            {error}
          </p>
        )}
      </div>
      )}
    </div>
  );
}

export default BatchUploadView;
